import { notifyBrandBackdrop } from '../brand-tone.js';
import { siteMotion } from '../site-motion.js';

export function startGradient(layer, { scroller = null, revealActivity } = {}) {
  if (!layer || layer.querySelector('canvas')) return () => {};
  const events = new AbortController();
  const { signal } = events;

  // Original WebGL interpretation of the supplied macOS-style layered landscape.
  // The flowing and reference renderers remain untouched in their own files.
  const settings = {
    colors: ['#CF42A3', '#B119CF', '#7506D2', '#4513AF', '#241168'],
    speed: 0.32, // Lower for slower movement; 0 pauses at the initial composition.
    grain: 0.003,
  };
  const motion = siteMotion;
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  layer.append(canvas);

  const vertexSource = `
    attribute vec2 a_position;
    varying vec2 v_uv;
    void main() {
      v_uv = a_position * 0.5 + 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;
  const fragmentSource = `
    precision highp float;
    varying vec2 v_uv;
    uniform float u_time;
    uniform float u_grain;
    uniform vec3 u_colors[5];

    float hash(vec2 p) {
      p = fract(p * vec2(0.1031, 0.0973));
      p += dot(p, p + 34.345);
      return fract(p.x * p.y);
    }

    // Each curtain has a broad bend and smaller waves that travel at different
    // rates. Boundaries deform continuously rather than sliding a bitmap.
    float curtain(float y, float t, float index) {
      return 0.235 + 0.49 * y
        + 0.046 * sin(y * 6.2 - t * 0.72 + index * 0.42)
        + 0.018 * sin(y * 13.0 + t * 0.46 + index * 0.8)
        + 0.07 * sin(t * 0.57 + index * 0.32);
    }

    void main() {
      vec2 p = vec2(v_uv.x, 1.0 - v_uv.y);
      float t = u_time;
      // Luminous opening between the two sets of folds.
      vec3 color = mix(vec3(0.76, 0.76, 0.86), vec3(0.85, 0.40, 0.66), p.y);
      float light = exp(-pow((p.x - 0.57) * 3.3, 2.0) - pow(p.y * 2.2, 2.0));
      color = mix(color, vec3(0.92, 0.83, 0.87), light * 0.62);

      // Rose curtains descend from the upper right, dissolving into violet below.
      for (int i = 0; i < 5; i++) {
        float f = float(i);
        float edge = 0.70 + f * 0.105 - 0.26 * p.y
          + 0.045 * sin(p.y * 5.0 + t * 0.43 + f * 0.55)
          + 0.025 * sin(p.y * 9.0 - t * 0.38 + f);
        vec3 rose = mix(vec3(0.86, 0.62, 0.74), vec3(0.81, 0.28, 0.62), f / 4.0);
        rose = mix(rose, vec3(0.63, 0.0, 0.88), smoothstep(0.50, 1.1, p.y));
        color = mix(color, rose, smoothstep(edge - 0.001, edge + 0.001, p.x) * 0.76);
      }
      color = mix(color, vec3(0.53, 0.0, 0.87), smoothstep(0.72, 1.15, p.y) * 0.85);

      // Layered foreground: pink ridge, orchid, electric violet, deep indigo.
      for (int i = 0; i < 7; i++) {
        float f = float(i);
        float edge = curtain(p.y, t, f) - f * (0.090 + 0.021 * p.y);
        float distance = edge - p.x;
        vec3 ridge;
        if (i == 0) ridge = u_colors[0];
        else if (i == 1) ridge = mix(u_colors[0], u_colors[1], 0.48);
        else if (i == 2) ridge = u_colors[1];
        else if (i == 3) ridge = u_colors[2];
        else if (i == 4) ridge = mix(u_colors[2], u_colors[3], 0.50);
        else if (i == 5) ridge = u_colors[3];
        else ridge = u_colors[4];
        ridge = mix(ridge, u_colors[4], smoothstep(0.2, 1.3, p.y) * 0.32);
        ridge += 0.035 * exp(-max(distance, 0.0) * 12.0);
        float softness = 0.000625 + 0.0005 * sin(f * 1.7 + p.y * 4.0) * sin(f * 1.7 + p.y * 4.0);
        color = mix(color, ridge, smoothstep(-softness, softness, distance));
      }
      // Stationary, very fine dither prevents banding without visible sparkle.
      color += (hash(gl_FragCoord.xy) - 0.5) * u_grain;
      gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
    }
  `;

  let gl = null;
  let program = null;
  let buffer = null;
  let timeLocation = null;
  let frame = 0;
  let lastDraw = null;
  let elapsed = 0;
  let hasDrawn = false;
  let needsResize = true;
  let failed = false;
  let pageActive = true;
  let inView = !('IntersectionObserver' in window);
  let dpr = 0;
  const frameInterval = 1000 / 30;

  function stop() {
    cancelAnimationFrame(frame);
    frame = 0;
    lastDraw = null;
  }

  function release() {
    stop();
    layer.classList.remove('is-ready');
    if (gl && !gl.isContextLost()) {
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
    }
    program = null;
    buffer = null;
    canvas.width = canvas.height = 1;
    needsResize = true;
    hasDrawn = false;
  }

  function compile(type, source) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error('Gradient shader unavailable');
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      throw new Error('Gradient shader unsupported');
    }
    return shader;
  }

  function initialize() {
    try {
      gl = gl || canvas.getContext('webgl', {
        alpha: false, antialias: false, depth: false, stencil: false,
        preserveDrawingBuffer: false, powerPreference: 'low-power',
      });
      if (!gl || gl.isContextLost()) return false;
      program = gl.createProgram();
      const shaders = [];
      try {
        shaders.push(compile(gl.VERTEX_SHADER, vertexSource));
        const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
        shaders.push(compile(gl.FRAGMENT_SHADER, precision && precision.precision
          ? fragmentSource : fragmentSource.replace('precision highp', 'precision mediump')));
        shaders.forEach(shader => gl.attachShader(program, shader));
        gl.linkProgram(program);
      } finally {
        shaders.forEach(shader => gl.deleteShader(shader));
      }
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Gradient link failed');
      gl.useProgram(program);
      buffer = gl.createBuffer();
      if (!buffer) throw new Error('Gradient buffer unavailable');
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      const colors = settings.colors.flatMap(hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255));
      gl.uniform3fv(gl.getUniformLocation(program, 'u_colors[0]'), new Float32Array(colors));
      gl.uniform1f(gl.getUniformLocation(program, 'u_grain'), settings.grain);
      timeLocation = gl.getUniformLocation(program, 'u_time');
      return true;
    } catch (_) {
      release();
      return false;
    }
  }

  function resize() {
    const width = layer.clientWidth;
    const height = layer.clientHeight;
    if (!width || !height) return false;
    dpr = window.devicePixelRatio || 1;
    // Retina up to 2x; cap the soft background at 1.5 MP, including large displays.
    const limit = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
    const scale = Math.min(dpr, 2, Math.sqrt(1500000 / (width * height)), limit[0] / width, limit[1] / height);
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    gl.viewport(0, 0, canvas.width, canvas.height);
    needsResize = false;
    return true;
  }

  function draw(now) {
    frame = 0;
    if (document.hidden || !pageActive || !inView || revealActivity?.active || motion.matches || failed || !program) return;
    if (lastDraw === null || now - lastDraw >= frameInterval - 1) {
      if ((needsResize || dpr !== (window.devicePixelRatio || 1)) && !resize()) {
        frame = requestAnimationFrame(draw);
        return;
      }
      if (lastDraw !== null) elapsed += Math.min(now - lastDraw, 100) / 1000;
      lastDraw = now;
      paintFrame();
    }
    frame = requestAnimationFrame(draw);
  }

  function paintFrame() {
    gl.uniform1f(timeLocation, elapsed * settings.speed);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    hasDrawn = true;
    layer.classList.add('is-ready');
    notifyBrandBackdrop(canvas);
  }

  function sync() {
    stop();
    if (motion.matches) {
      release();
      return;
    }
    if (document.hidden || !pageActive || failed) return;
    if (!program && !initialize()) {
      failed = true;
      return;
    }
    if (!inView || revealActivity?.active) {
      // 初回の表示前にMoveを1枚だけ用意する。以降の休止は最後の絵を保つ。
      if (!hasDrawn && resize()) paintFrame();
      return;
    }
    frame = requestAnimationFrame(draw);
  }

  canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    release();
    failed = true;
  }, { signal });
  canvas.addEventListener('webglcontextrestored', () => {
    failed = false;
    sync();
  }, { signal });
  window.addEventListener('resize', () => { needsResize = true; }, { passive: true, signal });
  const observer = 'ResizeObserver' in window ? new ResizeObserver(() => { needsResize = true; }) : null;
  observer?.observe(layer);
  const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting;
    sync();
  }, { root: scroller }) : null;
  visibilityObserver?.observe(layer);
  document.addEventListener('visibilitychange', sync, { signal });
  window.addEventListener('pagehide', () => { pageActive = false; stop(); }, { signal });
  window.addEventListener('pageshow', () => { pageActive = true; sync(); }, { signal });
  if (motion.addEventListener) motion.addEventListener('change', sync, { signal });
  else motion.addListener(sync);
  sync();
  const unsubscribe = revealActivity?.subscribe(sync);
  return () => {
    pageActive = false;
    events.abort();
    observer?.disconnect();
    visibilityObserver?.disconnect();
    unsubscribe?.();
    if (!motion.addEventListener) motion.removeListener(sync);
    release();
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    canvas.remove();
  };
}
